import { useCallback, useEffect, useRef, useState } from "react";

import { InteractionObjectType, InteractionType } from "../../constants/interaction";
import { SelectData } from "../../models";
import { searchInteractions } from "../../service/query/QueryService.full";
import { Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { jsonReviver } from "../../utils";
import { dataGridMapFilterCriteria } from "../../widget";

interface UseTaskTagsFilterReturnData {
  tagsFilterSelectData: SelectData;
  tagsFilterValues: string[];
  /*
    The selected tag uuids resolved to the WorkflowTask uuids they are linked to.
    undefined => no tag filter applied.
    []        => tags selected but nothing is tagged, so the grid should show no results.
  */
  tagTaskUuids: string[] | undefined;
  onTagsFilter: (keys: string[]) => Promise<void>;
}

/*
  Shared tag-filter behaviour for the task list/board, mirroring the project-tracker pattern:
  tags are org-level Interaction definitions read via searchInteractions, and filtering resolves
  the selected tags to the WorkflowTask uuids they are linked to (via objectInteractions).
  `onApplied` lets the caller trigger a grid refresh once the selection has been resolved.
  `refreshKey` re-reads the tag options whenever it changes — saving a task can create new org-level
  tags, so the caller passes its grid-refresh flag to keep the filter list in step with them.
*/
export const useTaskTagsFilter = (onApplied?: () => void, refreshKey?: unknown): UseTaskTagsFilterReturnData => {
  const { currentOrganisationUuid } = useAuth();

  const [tagsFilterSelectData, setTagsFilterSelectData] = useState<SelectData>([]);
  const [tagsFilterValues, setTagsFilterValues] = useState<string[]>([]);
  const [tagTaskUuids, setTagTaskUuids] = useState<string[] | undefined>(undefined);
  // Monotonic id of the latest tag-resolve request, so stale responses can be discarded.
  const requestIdRef = useRef(0);

  // Populate the tag options from the org-level tag definitions.
  const getOrganisationTags = useCallback(async (organisationUuid: string): Promise<void> => {
    const filterCriteria = dataGridMapFilterCriteria([]);
    filterCriteria.type = { operator: "eq", value: InteractionType.TAG };
    filterCriteria.objectType = { operator: "eq", value: InteractionObjectType.ORGANISATION };
    filterCriteria.objectUuid = { operator: "eq", value: organisationUuid };

    const response = await searchInteractions({
      paging: { beforeCursor: null, afterCursor: null, limit: 50 },
      filter: { results: filterCriteria },
    });

    if (response.status === Status.Success && response.data?.results != null && response.data.results.length > 0) {
      setTagsFilterSelectData(
        response.data.results.map((tag) => ({
          key: tag.uuid,
          value: JSON.parse(tag.data, jsonReviver).name,
        })) as SelectData
      );
    }
  }, []);

  // Resolve the selected tags to the WorkflowTask uuids they are linked to.
  const onTagsFilter = useCallback(
    async (keys: string[]): Promise<void> => {
      setTagsFilterValues(keys);

      // The selection can change (one click per tag) while a resolve is still in flight, so each
      // request is stamped and only the newest one is allowed to apply its result — otherwise a
      // slow response for a superseded selection would filter the grid by the wrong tags.
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;

      if (keys.length === 0) {
        setTagTaskUuids(undefined);
        onApplied?.();
        return;
      }

      const filterCriteria = dataGridMapFilterCriteria([]);
      filterCriteria.uuid = { operator: "in", value: keys };

      const response = await searchInteractions({
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: { results: filterCriteria },
      });

      if (requestId !== requestIdRef.current) return;

      const taskUuids =
        response.status === Status.Success && response.data?.results != null
          ? response.data.results
              .flatMap((tag) => tag.objectInteractions)
              // Untagging ends the link rather than deleting it, so an ended link must not match.
              .filter(
                (objectInteraction) =>
                  objectInteraction.objectType === InteractionObjectType.WORKFLOW_TASK &&
                  objectInteraction.endDate == null
              )
              .map((objectInteraction) => objectInteraction.objectUuid)
          : [];

      setTagTaskUuids(Array.from(new Set(taskUuids)));
      onApplied?.();
    },
    [onApplied]
  );

  useEffect(() => {
    if (currentOrganisationUuid) getOrganisationTags(currentOrganisationUuid);
  }, [currentOrganisationUuid, getOrganisationTags, refreshKey]);

  return { tagsFilterSelectData, tagsFilterValues, tagTaskUuids, onTagsFilter };
};
